import React from "react";

import { Panel, Eyebrow, FieldRow, Th } from "./Panel";
import InvoiceActionsDropdown from "../../InvoiceModal/Components/InvoiceSubComponents/InvoiceActionsWithDocs";
import { capitalize } from "../../../helpers/helpers";
import { useTranslation } from "react-i18next";

/**
 * Customs declaration.
 *
 * Reproduces International.jsx, including its RENDER-TIME MUTATION: that
 * component writes DeclaredValue onto each product and totalUnits /
 * totalDeclared onto `intl`, and the print path reads those back. A Customs
 * panel that rendered instead of International without doing the same would
 * leave them undefined on the PDF.
 *
 * Document upload / delete mounts the production InvoiceActionsDropdown, which
 * owns the 13-code DOCUMENT_TYPE_MAP and both endpoints. Its props are wired
 * exactly as EditableInvoice.jsx:67-88 wires them, including the Declaration
 * item, which downloads single_shipping_invoice through the shell's copy of
 * InvoiceModal's downloadDocs. Both Panels pass clip={false}: the dropdown is
 * absolute, and the Panel's overflow-hidden cut off the document list and its
 * trash icons.
 */
const CustomsPanel = ({ data, setData, downloadDocs }) => {
  const { t } = useTranslation("admin");
  const intl = data?.international;
  const isMulti = Array.isArray(intl?.products);

  // Same assignments as International.jsx:15-30, in the same order.
  if (isMulti) {
    const summary = intl.products.reduce(
      (acc, product) => {
        const count = Number(product?.Count || 0);
        const price = Number(product?.TotalPriceAmount || 0);
        const declared = Number((count * price).toFixed(2));
        product.DeclaredValue = declared;
        acc.totalUnits += count;
        acc.totalDeclared += declared;
        return acc;
      },
      { totalUnits: 0, totalDeclared: 0 },
    );
    intl.totalUnits = summary.totalUnits;
    intl.totalDeclared = Number(summary.totalDeclared.toFixed(2));
  }

  const isInternational = !(intl === null || intl === undefined || intl?.length === 0);
  const dutiesBy = intl?.duties_by
    ? t(`invoice.dutiesBy.${intl.duties_by}`, {
        defaultValue: capitalize(intl.duties_by),
      })
    : t("invoice.dutiesBy.receiver");

  const documentActions = data?.single_shipping_invoice ? (
    <InvoiceActionsDropdown
      documents={data?.shipping_document || []}
      shipmentId={data?.id}
      onDownloadDeclaration={() => downloadDocs(data, "declaration")}
      onDownloadAllZip={() => {}}
      onDocumentsUpdate={(documents) =>
        setData((prev) => ({ ...prev, shipping_document: documents }))
      }
      onDocumentDelete={(deleteId) =>
        setData((prev) => ({
          ...prev,
          shipping_document: prev.shipping_document.filter(
            (d) => d.id !== deleteId,
          ),
        }))
      }
    />
  ) : null;

  if (!isInternational) {
    return (
      <Panel
        title={t("invoice.customs")}
        chip={t("invoice.domesticChip")}
        chipTone="neutral"
        hint={t("invoice.noDeclarationOnThisShipment")}
        actions={documentActions}
        clip={false}
      >
        <div className="px-2.5 py-4 text-center font-manrope text-[11px] font-medium text-ui-text-muted">
          {t("invoice.domesticNote")}
        </div>
      </Panel>
    );
  }

  return (
    <Panel
      title={t("invoice.customsDeclaration")}
      chip={
        isMulti
          ? t("invoice.unitsDeclared", { units: intl.totalUnits, value: intl.totalDeclared })
          : t("invoice.singleProduct")
      }
      chipTone="sky"
      hint={t("invoice.dutiesByHint", { by: dutiesBy })}
      actions={documentActions}
      clip={false}
    >
      {isMulti ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-max border-collapse tabular-nums">
              <thead>
                <tr className="border-b border-ui-border-strong bg-ui-surface-sunken">
                  <Th align="center">#</Th>
                  <Th align="right">{t("invoice.units")}</Th>
                  <Th>{t("invoice.description")}</Th>
                  <Th>{t("invoice.commodityCode")}</Th>
                  <Th>{t("common:fields.country")}</Th>
                  <Th align="right">{t("invoice.unitValue")}</Th>
                  <Th align="right">{t("invoice.totalDeclaredValue")}</Th>
                </tr>
              </thead>
              <tbody className="font-manrope text-[11.5px] font-medium text-ui-text">
                {intl.products.map((p, index) => (
                  <tr
                    key={index}
                    className="border-b border-ui-border-subtle hover:bg-ui-row-hover"
                  >
                    <td className="px-[9px] py-1 text-center text-ui-text-muted">
                      {index + 1}
                    </td>
                    <td className="px-[9px] py-1 text-right">{p?.Count}</td>
                    <td className="px-[9px] py-1 text-ui-text-secondary">
                      {p?.Description}
                    </td>
                    <td className="px-[9px] py-1 text-ui-text-secondary">
                      {p?.CommodityCode}
                    </td>
                    <td className="px-[9px] py-1 text-ui-text-secondary">
                      {p?.Country}
                    </td>
                    <td className="px-[9px] py-1 text-right text-ui-text-secondary">
                      {p?.TotalPriceAmount}
                    </td>
                    <td className="px-[9px] py-1 text-right font-semibold">
                      {p?.TotalPriceAmount} x {p?.Count} = {p?.DeclaredValue} CAD
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid gap-px border-t border-ui-border bg-ui-border [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
            <div className="min-w-0 bg-ui-surface px-2.5 py-2">
              <Eyebrow size="sm" className="mb-1.5">{t("invoice.producerInformation")}</Eyebrow>
              <FieldRow
                label={t("invoice.companyName")}
                value={intl?.producer?.CompanyName}
              />
              <FieldRow label={t("common:fields.country")} value={intl?.producer?.Country} />
            </div>
            <div className="min-w-0 bg-ui-surface px-2.5 py-2">
              <Eyebrow size="sm" className="mb-1.5">{t("invoice.additionalInformation")}</Eyebrow>
              <FieldRow
                label={t("invoice.insuranceAmount")}
                value={intl?.InsuranceAmount}
              />
              <FieldRow
                label={t("invoice.reasonForExport")}
                value={intl?.ReasonForExport}
              />
              <FieldRow
                label={t("invoice.totalDeclaredUnits")}
                value={intl?.totalUnits}
              />
              <FieldRow
                label={t("invoice.totalDeclaredValue")}
                value={
                  intl?.totalDeclared != null
                    ? `${intl.totalDeclared} CAD`
                    : null
                }
              />
              <FieldRow label={t("invoice.dutiesByLabel")} value={dutiesBy} />
            </div>
          </div>
        </>
      ) : (
        <div className="grid gap-px bg-ui-border [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
          <div className="min-w-0 bg-ui-surface px-2.5 py-2">
            <Eyebrow size="sm" className="mb-1.5">{t("invoice.productInformation")}</Eyebrow>
            {[
              "Description",
              "Country",
              "InsuranceAmount",
              "ReasonForExport",
              "TotalPriceAmount",
            ].map((key) => (
              <FieldRow
                key={key}
                label={t(`invoice.productFields.${key}`, {
                  defaultValue:
                    key === "TotalPriceAmount"
                      ? "Monetary Amount"
                      : capitalize(key.replace(/([A-Z])/g, " $1")),
                })}
                value={intl?.product?.[key]}
              />
            ))}
            <FieldRow label={t("invoice.dutiesByLabel")} value={dutiesBy} />
          </div>
          <div className="min-w-0 bg-ui-surface px-2.5 py-2">
            <Eyebrow size="sm" className="mb-1.5">{t("invoice.producerInformation")}</Eyebrow>
            <FieldRow
              label={t("invoice.companyName")}
              value={intl?.producer?.CompanyName}
            />
            <FieldRow label={t("common:fields.country")} value={intl?.producer?.Country} />
          </div>
        </div>
      )}
    </Panel>
  );
};

export default CustomsPanel;
